import { Trigger, Content } from '@radix-ui/react-tooltip';
import styled, { css, keyframes } from 'styled-components';
import { caption, spacing, colors, zIndex, borderRadius, darkOverlayShadow } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { TooltipProps, TooltipContentProps, FocusStyle } from './types';

const slideUpAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideRightAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(-2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const slideDownAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(-2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideLeftAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const FOCUS_PADDING = 1;
const FOCUS_BORDER_WIDTH = 1.5;

const defaultMixin = css`
  display: inline-block;
`;

const roundMixin = css`
  display: inline-flex;
  &:focus {
    outline: none;
    &:before {
      border-radius: 50%;
      border: ${pxToRem(FOCUS_BORDER_WIDTH)} solid ${colors.purple50};
      box-sizing: border-box;
      content: '';
      inset: -${pxToRem(FOCUS_PADDING)};
      position: absolute;
    }
  }
`;

const themeStyles = {
  [FocusStyle.Default]: defaultMixin,
  [FocusStyle.Round]: roundMixin,
};

const underlineStyle = css`
  text-decoration: underline;
  text-decoration-style: dashed;
  text-underline-offset: ${spacing.eighth};
`;

export const StyledTooltipTrigger = styled(Trigger)<{
  $focusStyle: TooltipProps['$focusStyle'];
  $isUnderlined: TooltipProps['isUnderlined'];
  $isUnderlineGrey?: boolean;
}>`
  position: relative;
  cursor: pointer;
  outline: none;
  ${({ $isUnderlined }) => ($isUnderlined ? underlineStyle : ``)}
  ${({ $isUnderlineGrey }) =>
    $isUnderlineGrey ? `text-decoration-color: ${colors.borderEmphasis};` : ``}
  ${({ $focusStyle }) => themeStyles[$focusStyle || FocusStyle.Default]}
`;

const tooltipAlign = css`
  &[data-align='start'],
  &[data-align='end'] {
    > span:first-of-type {
      /* 
       10px is the width of the arrow, so 5px is half of thus (centering the
       arrow).  This should eventually be done dynamically. 
       */
      left: calc(50% - 5px) !important;
    }
  }
`;

export const TooltipContent = styled(Content)<TooltipContentProps>`
  ${caption}
  align-items: center;
  animation-duration: ${({ $animationDuration }) => $animationDuration}ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  box-sizing: border-box;
  color: ${colors.textOnColor};
  border-radius: ${borderRadius.lg};
  background: ${colors.backgroundDarkOverlay};
  backdrop-filter: blur(${spacing.half});
  ${darkOverlayShadow}
  display: flex;
  justify-content: flex-start;
  max-width: ${spacing.x16};
  word-break: break-word;
  padding: ${spacing.quarter} ${spacing.threeQuarter};
  will-change: transform, opacity;
  z-index: ${zIndex.tooltip};
  &[data-side='top'] {
    animation-name: ${slideUpAndFade};
    ${tooltipAlign}
  }
  &[data-side='bottom'] {
    animation-name: ${slideDownAndFade};
    ${tooltipAlign}
  }
  &[data-side='right'] {
    animation-name: ${slideRightAndFade};
  }
  &[data-side='left'] {
    animation-name: ${slideLeftAndFade};
  }
`;

export const ToggletipContainer = styled.div`
  margin: 0;
  height: fit-content;
  display: inline-flex;
`;
